import { Key, useEffect, useState, useCallback } from "react";
import { TablePaginationConfig } from "antd";
import api from "../../services/api";
import { notifyError } from "../../utils/notifications";
import { formatDateTimeSeconds } from "../../utils/formatDate";
import { TServiceProtocols } from "./types";

const useServiceProtocols = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [serviceProtocolData, setServiceProtocolData] = useState<
    TServiceProtocols[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const onSelectChange = (newSelectedRowKeys: Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const fetchServiceProtocols = useCallback(
    async (page: number, pageSize: number) => {
      setLoading(true);
      try {
        const response = await api.get("protocolo_atendimento", {
          page,
          limit: pageSize,
        });

        const { data, meta } = response;

        setServiceProtocolData(data);
        setPagination({
          current: Number(meta.page || meta.current_page || 1),
          pageSize: Number(meta.perPage || meta.per_page || 10),
          total: Number(meta.total || 0),
        });
      } catch (error) {
        notifyError("Erro ao buscar protocolos de atendimento");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const paginationConfig = {
    ...pagination,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
    showTotal: (total: number, range: number[]) =>
      `Mostrando ${range[0]} a ${range[1]} de ${total} registros`,
  };

  const mappedServiceProtocol = serviceProtocolData.map((item) => {
    return {
      numeroProtocolo: item.numeroProtocolo,
      idPortador: item.idPortador,
      nomePortador: item?.nomePortador,
      status: item.status,
      dataCriacao: item.dataCriacao
        ? formatDateTimeSeconds(item.dataCriacao as string)
        : null,
      criadoPor: item.criadoPor,
      dataFinalizacao: item.dataFinalizacao
        ? formatDateTimeSeconds(item.dataFinalizacao as string)
        : null,
      finalizadoPor: item.finalizadoPor,
    };
  });

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    if (
      newPagination.current !== pagination.current ||
      newPagination.pageSize !== pagination.pageSize
    ) {
      fetchServiceProtocols(
        newPagination.current || 1,
        newPagination.pageSize || 10
      );
    }
  };

  useEffect(() => {
    fetchServiceProtocols(pagination.current, pagination.pageSize);
  }, []);

  return {
    rowSelection,
    paginationConfig,
    mappedServiceProtocol,
    loading,
    handleTableChange,
  };
};

export default useServiceProtocols;
